import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { ApiError } from '@/api/client';
import type { Vacancy, VacancyWrite, WebsiteBlock } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { EditorColumns, EditorPage } from '@/components/layout/EditorPage';
import { Panel } from '@/components/layout/Page';
import { useUnsavedChanges } from '@/lib/useUnsavedChanges';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { Select } from '@/components/ui/Select';
import { apiUrl } from '@/lib/env';

import { useSaveVacancy } from '../api';

const DETAIL_FIELDS = [
  { key: 'industry', label: 'Industry', required: true, max: 120 },
  { key: 'country', label: 'Country', required: true, max: 120 },
  { key: 'location', label: 'City or location', required: false, max: 200 },
  { key: 'employment_type', label: 'Employment type', required: true, max: 80 },
  { key: 'salary', label: 'Salary and currency', required: false, max: 200 },
] as const;

function initialContent(
  text: string | undefined,
  blocks: WebsiteBlock[] | undefined,
): WebsiteBlock[] {
  if (blocks?.length) return blocks;
  return text ? [{ type: 'paragraph', text, alt: '', ordered: false }] : [];
}

export function VacancyForm({
  vacancy,
  onClose,
}: {
  vacancy: Vacancy | null;
  onClose: () => void;
}) {
  const formId = useId();
  const save = useSaveVacancy();
  const [draft, setDraft] = useState<VacancyWrite>(() => ({
    title: vacancy?.title ?? '',
    employer: vacancy?.employer ?? '',
    industry: vacancy?.industry ?? '',
    country: vacancy?.country ?? '',
    location: vacancy?.location ?? '',
    employment_type: vacancy?.employment_type ?? 'Full time',
    salary: vacancy?.salary ?? '',
    highlighted: vacancy?.highlighted ?? false,
    openings: vacancy?.openings ?? 1,
    description: vacancy?.description ?? '',
    requirements: vacancy?.requirements ?? '',
    description_blocks: vacancy?.description_blocks ?? [],
    requirements_blocks: vacancy?.requirements_blocks ?? [],
    closing_on: vacancy?.closing_on ?? null,
    state: vacancy?.state ?? 'published',
  }));
  const [changed, setChanged] = useState(false);
  const unsaved = useUnsavedChanges(changed || save.isPending);
  const [descriptionError, setDescriptionError] = useState('');
  const staged = useRef(new Map<string, { file: File; url: string }>());
  useEffect(() => {
    const assets = staged.current;
    return () => {
      assets.forEach(({ url }) => {
        URL.revokeObjectURL(url);
      });
      assets.clear();
    };
  }, []);
  const imageUrl = (id: string) =>
    staged.current.get(id)?.url ??
    apiUrl(`/api/v1/careers/vacancies/${vacancy?.id}/images/${id}`);
  const selectImage = (file: File) => {
    if (file.size === 0 || file.size > 5 * 1024 * 1024) {
      return Promise.reject(new Error('Choose an image between 1 byte and 5 MB.'));
    }
    const id = crypto.randomUUID();
    staged.current.set(id, { file, url: URL.createObjectURL(file) });
    return Promise.resolve(id);
  };
  const setContent = (field: 'description' | 'requirements', blocks: WebsiteBlock[]) => {
    const plain = blocks
      .flatMap((block) =>
        block.type === 'image'
          ? []
          : block.type === 'list'
            ? [
                (block.items ?? [])
                  .map((item, index) => (block.ordered ? `${index + 1}. ${item}` : `• ${item}`))
                  .join('\n'),
              ]
            : [block.text],
      )
      .join('\n\n');
    setDraft((current) => ({ ...current, [field]: plain, [`${field}_blocks`]: blocks }));
    if (field === 'description') setDescriptionError('');
    setChanged(true);
  };
  const change = <K extends keyof VacancyWrite>(key: K, value: VacancyWrite[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setChanged(true);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.description.trim()) {
      setDescriptionError('Enter a job description.');
      return;
    }
    const imageIds = new Set(
      [...(draft.description_blocks ?? []), ...(draft.requirements_blocks ?? [])].flatMap(
        (block) => (block.image_id ? [block.image_id] : []),
      ),
    );
    const media = Array.from(imageIds).flatMap((id) => {
      const asset = staged.current.get(id);
      return asset ? [{ id, file: asset.file }] : [];
    });
    save.mutate(
      { id: vacancy?.id, values: draft, ...(media.length ? { media } : {}) },
      {
        onSuccess: () => {
          unsaved.allowSavedExit();
          onClose();
        },
      },
    );
  };
  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <EditorPage
      title={vacancy ? 'Edit vacancy' : 'Create vacancy'}
      description="Published vacancies are available for staff to share with candidates."
      backTo="/careers"
      backLabel="Back to Vacancies"
      busy={save.isPending}
      unsaved={unsaved}
      actions={
        <>
          <Button variant="ghost" disabled={save.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={save.isPending}>
            Save vacancy
          </Button>
        </>
      }
    >
      {save.isError ? (
        <Alert tone="danger">{error?.message ?? 'The vacancy could not be saved.'}</Alert>
      ) : null}
      <form id={formId} onSubmit={submit}>
        <EditorColumns
          settings={
            <>
              <Panel title="Publishing">
                <Field label="Visibility">
                  {(ids) => (
                    <Select
                      {...ids}
                      value={draft.state}
                      disabled={save.isPending}
                      options={[
                        { value: 'draft', label: 'Draft' },
                        { value: 'published', label: 'Published' },
                        { value: 'closed', label: 'Inactive' },
                      ]}
                      onChange={(event) => {
                        change('state', event.target.value as Vacancy['state']);
                      }}
                    />
                  )}
                </Field>
                <Checkbox
                  label="Highlight on the public Jobs page"
                  checked={draft.highlighted}
                  disabled={save.isPending}
                  onChange={(event) => {
                    change('highlighted', event.target.checked);
                  }}
                />
              </Panel>
              <Panel title="Job details">
                {DETAIL_FIELDS.map(({ key, label, required, max }) => (
                  <Field
                    key={key}
                    label={label}
                    required={required}
                    error={error?.fieldErrors.find((item) => item.field === key)?.message}
                  >
                    {(ids) => (
                      <Input
                        {...ids}
                        required={required}
                        maxLength={max}
                        disabled={save.isPending}
                        value={draft[key]}
                        onChange={(event) => {
                          change(key, event.target.value);
                        }}
                      />
                    )}
                  </Field>
                ))}
              </Panel>
            </>
          }
        >
          <Panel>
            <Field
              label="Job title"
              required
              error={error?.fieldErrors.find((item) => item.field === 'title')?.message}
            >
              {(ids) => (
                <Input
                  {...ids}
                  required
                  maxLength={200}
                  disabled={save.isPending}
                  value={draft.title}
                  onChange={(event) => {
                    change('title', event.target.value);
                  }}
                />
              )}
            </Field>
          </Panel>
          <RichTextEditor
            label="Job description"
            required
            error={
              descriptionError ||
              error?.fieldErrors.find((item) => item.field === 'description')?.message
            }
            initial={initialContent(vacancy?.description, vacancy?.description_blocks)}
            busy={save.isPending}
            imageUrl={imageUrl}
            selectImage={selectImage}
            onChange={(blocks) => {
              setContent('description', blocks);
            }}
          />
          <RichTextEditor
            label="Experience and requirements"
            initial={initialContent(vacancy?.requirements, vacancy?.requirements_blocks)}
            busy={save.isPending}
            imageUrl={imageUrl}
            selectImage={selectImage}
            onChange={(blocks) => {
              setContent('requirements', blocks);
            }}
          />
        </EditorColumns>
      </form>
    </EditorPage>
  );
}
